// Данные → Недопродажи (Алексей 28.10, #3181–#3185, название #3209): во сколько позицию перестают брать и сколько,
// похоже, недопродали, когда её не было, а спрос был. Тепловая карта позиций × дни = выбор позиции; по
// выбору — одно поле за период: дни колонками, время дня сверху вниз, точки — продажи, красное — «сухие
// окна» (ждали ≥ min_expected чеков с позицией, не было ни одного). Расчёт — ops_tasks/api/item_gaps.py.
import { LostSalesCard } from '@/components/speech/LostSalesCard';
import { Select } from '@/components/ui/select';
import React from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Area, CartesianGrid, ComposedChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowUpRight, Check, MousePointerClick, Plus, X } from 'lucide-react';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';
import { ClipExample } from '@/pages/Management/ConversationsOverview';

import { Card, CardContent } from '@/components/ui/card';
import { getItemGaps, type ItemGapsDto, type ItemGapsItemDto } from '@/api/receipts';
import { cn } from '@/lib/utils';
import { money } from '@/lib/finance';

const rub = (n: number) => money(`${Math.round(n).toLocaleString('ru-RU')} ₽`);
const hhmm = (m: number | null | undefined) =>
  m == null ? '—' : `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;
const dayLabel = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
const dayShort = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return { n: d.getDate(), wd: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][d.getDay()], we: d.getDay() === 0 || d.getDay() === 6 };
};
// Ссылка на «Выкладку» по стеллажу — в новой вкладке, из текущего URL (зона та же); день и час — если заданы
// (Алексей 03.11 #3537: «ссылка конкретно на этот день, а не внизу на весь период»).
const layoutHref = (pathname: string, sp: URLSearchParams, rack: number, day?: string, h?: number) => {
  const n = new URLSearchParams(sp);
  n.set('tab', 'layout');
  n.set('rack', String(rack));
  for (const k of ['item', 'over', 'by', 'conv', 'time']) n.delete(k);
  if (day) { n.set('from', day); n.set('to', day); }
  if (day && h != null) n.set('time', `${String(h).padStart(2, '0')}00-${h + 1 >= 24 ? '2359' : `${String(h + 1).padStart(2, '0')}00`}`);
  return `${pathname}?${n}`;
};
const fmtSwing = (m: number | null) => (m == null ? '' : m >= 90 ? `±${(m / 60).toFixed(1).replace('.', ',')} ч` : `±${m} мин`);
// Одна краска (красный) по силе: сколько недопродали в этот день, к максимуму по таблице.
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-card)', color: 'var(--color-foreground)' };
const TICK = { fontSize: 10, fill: 'var(--color-muted-foreground)' };
const lossBg = (v: number, max: number) => (v > 0 && max > 0 ? `rgb(239 68 68 / ${(0.18 + 0.72 * Math.min(1, v / max)).toFixed(2)})` : undefined);

// Раньше обычного — красным, позже — синим; ±3 ч = полный цвет (#3181: «во сколько прекращаются продажи»).
const lastBg = (last: number | null | undefined, med: number | null) => {
  if (last == null || med == null) return undefined;
  const d = Math.max(-1, Math.min(1, (last - med) / 180));
  if (Math.abs(d) < 0.08) return 'rgb(148 163 184 / 0.35)';
  return d < 0 ? `rgb(239 68 68 / ${(0.15 + 0.75 * -d).toFixed(2)})` : `rgb(37 99 235 / ${(0.15 + 0.6 * d).toFixed(2)})`;
};

// Строка — выбор позиции; «+» (или Ctrl/⌘-клик) добавляет её к выбранным: продажи складываются, чтобы видеть
// каннибализацию заменителей (Алексей 03.11: «А + Б, хот-доги и сэндвич-ролл с курицей»).
function Heatmap({ data, items, selected, onSelect, onToggle, paint }: {
  data: ItemGapsDto; items: ItemGapsItemDto[]; selected: string[]; onSelect: (n: string) => void; onToggle: (n: string) => void; paint: 'loss' | 'last';
}) {
  const max = Math.max(1, ...items.flatMap((it) => data.days.map((d) => it.by_day[d.day]?.lost_rub ?? 0)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-[2px] text-xs">
        <thead>
          <tr className="text-[10px] text-muted-foreground">
            <th className="sticky left-0 z-10 bg-card text-left font-normal">Позиция</th>
            {data.days.map((d) => {
              const s = dayShort(d.day);
              return (
                <th key={d.day} className={cn('min-w-[22px] font-normal leading-tight', s.we && 'text-foreground')}>
                  {s.n}<br />{s.wd}
                </th>
              );
            })}
            {/* «Непонятно, что написано в столбце» (Алексей 03.11 #3538): в ячейке только сумма и штуки, остальное —
                в подсказке; «Последняя» убрана — она в режиме «Последняя продажа» и в шапке позиции. */}
            <UiTooltip content={<span className="block max-w-72 text-xs leading-snug">Сколько, похоже, недопродали за весь период: часы, когда позиции не было, а её обычно берут. Штуки — сколько ждали продать в эти часы. Наведите на сумму — там оценка с вечерним дефицитом и сравнение с другими вашими точками.</span>}>
              <th className="cursor-help whitespace-nowrap pl-2 text-right font-normal underline decoration-dotted underline-offset-2">Недопродано за период</th>
            </UiTooltip>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const on = selected.includes(it.name);
            const upper = (it.upper_rub ?? 0) > it.lost_rub + 1 ? it.upper_rub! : null;
            const net = (it.network?.gap_rub ?? 0) > 0 ? it.network! : null;
            const lost = it.lost_rub > 0
              ? <>≈ {rub(it.lost_rub)} <span className="text-muted-foreground">· {it.lost_qty.toLocaleString('ru-RU')} шт</span></>
              : <span className="text-muted-foreground">—</span>;
            return (
              <tr
                key={it.name}
                onClick={(e) => (e.ctrlKey || e.metaKey ? onToggle(it.name) : onSelect(it.name))}
                className="group cursor-pointer"
              >
                <td className={cn('sticky left-0 z-10 max-w-[240px] rounded bg-card px-1', on ? 'bg-primary/10 font-medium text-primary' : 'group-hover:text-primary')}>
                  <div className="flex min-w-0 items-center gap-1">
                    <UiTooltip content={it.name} openDelay={400}>
                      <span className="min-w-0 flex-1 truncate">{it.name}</span>
                    </UiTooltip>
                    <UiTooltip content={on && selected.length > 1 ? 'Убрать из суммы' : 'Добавить к выбранным: продажи сложатся (видно, куда ушёл спрос). Или Ctrl/⌘ + клик по строке'}>
                      <button type="button" aria-label={on ? 'Убрать из суммы' : 'Добавить к сумме'}
                        onClick={(e) => { e.stopPropagation(); onToggle(it.name); }}
                        className={cn('shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground',
                          on && selected.length > 1 ? 'text-primary' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100')}>
                        {on && selected.length > 1 ? <Check className="size-3" /> : <Plus className="size-3" />}
                      </button>
                    </UiTooltip>
                  </div>
                </td>
                {data.days.map((d) => {
                  const c = it.by_day[d.day];
                  const sold = (c?.qty ?? 0) > 0;
                  const title = `${dayLabel(d.day)}: ${sold ? `${c!.qty.toLocaleString('ru-RU')} шт, последняя ${hhmm(c!.last)}` : 'не продавали'}`
                    + (c?.lost_rub ? ` · недопродано ≈ ${rub(c.lost_rub)}` : '');
                  return (
                    <UiTooltip key={d.day} content={title} openDelay={0}>
                      <td
                        className={cn('h-5 rounded-[3px]', sold ? 'bg-muted' : 'bg-muted/40')}
                        style={{ background: paint === 'loss' ? lossBg(c?.lost_rub ?? 0, max) : lastBg(sold ? c!.last : null, it.last_median) }}
                      />
                    </UiTooltip>
                  );
                })}
                {upper || net ? (
                  <UiTooltip content={
                    <span className="block max-w-80 space-y-1 text-xs leading-snug">
                      <span className="block">≈ {rub(it.lost_rub)} — часы, когда позиции не было, а её обычно берут ({it.lost_qty.toLocaleString('ru-RU')} шт).</span>
                      {upper && <span className="block">С вечерним дефицитом — до {rub(upper)}: если бы вечер шёл как в дни, когда позиция была до закрытия.</span>}
                      {net && <span className="block">До середины ваших точек — ещё +{rub(net.gap_rub)}: если бы на 100 чеков продавали как средняя ваша точка (здесь {net.own}, у середины {net.peers} других точек {net.median}).</span>}
                    </span>
                  }>
                    <td className="whitespace-nowrap pl-2 text-right tabular-nums">
                      <span className="underline decoration-dotted decoration-muted-foreground/60 underline-offset-2">{lost}</span>
                    </td>
                  </UiTooltip>
                ) : (
                  <td className="whitespace-nowrap pl-2 text-right tabular-nums">{lost}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type Det = NonNullable<ItemGapsDto['detail']>;
type Cell = { day: string; h: number; q: number; top: number; ratio: number | null; lay?: { pct: number; bad: number; n: number }; ups?: { n: number; any: number; offer: number } };
/** Наложение поверх продаж (#3279): апселл в целом или «предлагали эту позицию». */
type Over = 'upsell' | 'offer' | null;
const BAD_SHARE = 0.5;   // час с проблемной выкладкой — половина оценок «не полная»

// Ячейки «день × час»: только полные часы работы кассы этого дня. top — среднее 5 лучших дней в этот час.
function buildCells(data: ItemGapsDto, det: Det): Cell[] {
  const hoursOf = (d: { open: number; close: number }) => {
    const hs: number[] = [];
    for (let h = Math.floor(d.open / 60); h * 60 < d.close; h += 1) hs.push(h);
    return hs;
  };
  const byHour = new Map<number, number[]>();
  for (const d of data.days) for (const h of hoursOf(d)) {
    const arr = byHour.get(h) ?? [];
    arr.push(det.hourly[d.day]?.[h] ?? 0);
    byHour.set(h, arr);
  }
  const top = new Map<number, number>();
  byHour.forEach((v, h) => {
    const t = [...v].sort((a, b) => b - a).slice(0, 5);
    top.set(h, t.reduce((a, b) => a + b, 0) / Math.max(1, t.length));
  });
  return data.days.flatMap((d) => hoursOf(d).map((h) => {
    const q = det.hourly[d.day]?.[h] ?? 0;
    const tp = top.get(h) ?? 0;
    return { day: d.day, h, q, top: tp, ratio: tp > 0 ? q / tp : null, lay: det.layout?.[d.day]?.[h], ups: det.upsell?.[d.day]?.[h] };
  }));
}

// Связь выкладки и продаж на ходу (#3187): в часах с проблемной выкладкой продажи к топ-5 против остальных + r.
function layoutLink(cells: Cell[]) {
  const c = cells.filter((x) => x.lay && x.lay.n > 0 && x.ratio != null);
  if (c.length < 8) return null;
  const bad = c.filter((x) => x.lay!.bad >= BAD_SHARE), ok = c.filter((x) => x.lay!.bad < BAD_SHARE);
  const avg = (a: Cell[]) => (a.length ? a.reduce((s, x) => s + x.ratio!, 0) / a.length : null);
  return { n: c.length, nBad: bad.length, badRatio: avg(bad), okRatio: avg(ok) };
}

const pct = (v: number) => `${Math.round(v * 100)} %`;
function HatchSwatch({ color }: { color: string }) {
  return (
    <span className="inline-block size-3 rounded-sm align-[-2px]"
      style={{ background: `repeating-linear-gradient(45deg, ${color} 0 1.5px, transparent 1.5px 4px)`, outline: `1px solid ${color}40` }} />
  );
}

// Счёт часов под легендой: сколько проблемных часов с продажами и без, против доли пустых часов вообще.
function HatchCounts({ cells }: { cells: Cell[] }) {
  const bad = cells.filter((c) => c.lay && c.lay.bad >= BAD_SHARE);
  const ok = cells.filter((c) => c.lay && c.lay.bad < BAD_SHARE);
  if (!bad.length) return null;
  const badNone = bad.filter((c) => c.q <= 0).length;
  const okNone = ok.filter((c) => c.q <= 0).length;
  const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
  return (
    <span>
      {' '}(без продаж {pct(badNone, bad.length)} % из {bad.length} ч с проблемной выкладкой
      {ok.length ? ` против ${pct(okNone, ok.length)} % в остальные часы` : ''})
    </span>
  );
}

// Разница с 5 лучшими днями в ячейке: «−2», «+1,5».
const fmtDiff = (d: number) => `${d > 0 ? '+' : '−'}${Math.abs(d) >= 3 ? Math.round(Math.abs(d)) : Math.abs(d).toFixed(1).replace('.', ',').replace(',0', '')}`;

// Поле за период: колонка = день, строка = час, ячейка — продажи за час (штуки или к 5 лучшим дням в этот час);
// красная рамка — окно, где позиции, похоже, не было; штриховка — проблемная выкладка выбранного стеллажа.
// Светло-зелёная: тёмная терялась на синем (Алексей 03.11 #3537)
const HATCH_SOLD = '#86efac';
const HATCH_NONE = '#9333ea';

// «К 5 лучшим» — к обычному для позиции уровню (Алексей 03.11 #3537: «к лучшим 5 дням не может быть зелёное»):
// обычное = медиана доли от 5 лучших по всем часам (у редкой позиции медиана 0 — тогда среднее).
function typicalRatio(cells: Cell[]) {
  const r = cells.filter((c) => c.ratio != null).map((c) => c.ratio!).sort((a, b) => a - b);
  if (!r.length) return 0;
  const med = r[Math.floor(r.length / 2)];
  return med >= 0.05 ? med : r.reduce((a, b) => a + b, 0) / r.length;
}
// r = доля / обычная: 0,85–1,15 — как обычно (серое), ниже — оранжевое → красное по глубине, выше — синее.
const top5Fill = (r: number) => {
  if (r >= 0.85 && r <= 1.15) return 'rgb(148 163 184 / 0.3)';
  if (r > 1.15) return `rgb(37 99 235 / ${(0.2 + 0.55 * Math.min(1, (r - 1.15) / 0.85)).toFixed(2)})`;
  if (r >= 0.5) return `rgb(249 115 22 / ${(0.2 + 0.35 * (0.85 - r) / 0.35).toFixed(2)})`;
  return `rgb(239 68 68 / ${(0.4 + 0.45 * (0.5 - r) / 0.5).toFixed(2)})`;
};

function PeriodChart({ data, mode, showLayout, over, rackName }: { data: ItemGapsDto; mode: 'qty' | 'top5'; showLayout: boolean; over: Over; rackName: string }) {
  const det = data.detail!;
  const cells = React.useMemo(() => buildCells(data, det), [data, det]);
  const typical = React.useMemo(() => typicalRatio(cells), [cells]);
  const { pathname } = useLocation();
  const [hoverCell, setHover] = React.useState<Cell | null>(null);
  // Клик закрепляет панель справа (Алексей 29.10 #3243: «по хаверу моргает»); пока закреплена — наведение её не меняет.
  const [pinned, setPinned] = React.useState<Cell | null>(null);
  const hover = pinned ?? hoverCell;
  const [sp, setSp] = useSearchParams();
  const openConv = (id: number) => setSp((p) => { const n = new URLSearchParams(p); n.set('tab', 'conversations'); n.set('conv', String(id)); return n; });
  const h0 = Math.min(...data.days.map((d) => Math.floor(d.open / 60)));
  const h1 = Math.max(...data.days.map((d) => Math.ceil(d.close / 60)));
  const n = data.days.length;
  const L = 40, T = 6, colW = Math.max(16, Math.min(44, 900 / n)), rowH = 20, B = 30;
  const W = L + n * colW + 4, H = (h1 - h0) * rowH;
  const xOf = new Map(data.days.map((d, i) => [d.day, L + i * colW]));
  const yH = (h: number) => T + (h - h0) * rowH;
  const yM = (m: number) => T + (m / 60 - h0) * rowH;
  const maxQ = Math.max(1, ...cells.map((c) => c.q));
  const fill = (c: Cell) => {
    if (mode === 'qty') return c.q > 0 ? `rgb(37 99 235 / ${(0.15 + 0.75 * c.q / maxQ).toFixed(2)})` : 'var(--color-muted)';
    if (c.ratio == null || typical <= 0) return 'var(--color-muted)';
    return top5Fill(c.ratio / typical);
  };
  const hAsks = hover ? (det.asks ?? []).filter((a) => a.day === hover.day && Math.floor(a.minute / 60) === hover.h) : [];
  const cellAt = (day: string, h: number) => cells.find((c) => c.day === day && c.h === h) ?? null;
  const hGaps = hover ? det.gaps.filter((g) => g.day === hover.day && g.from < (hover.h + 1) * 60 && g.to > hover.h * 60) : [];
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${T + H + B}`} className="h-auto w-full" style={{ maxWidth: W * 1.1 }} onMouseLeave={() => setHover(null)}>
        <defs>
          {/* Проблемная выкладка двумя цветами (Алексей 30.10 #3426): продажи в этот час были — зелёная,
              не было — фиолетовая, чтобы глазами видеть, совпадают ли просадка выкладки и пустые часы. */}
          <pattern id="ig-hatch-sold" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="5" stroke={HATCH_SOLD} strokeWidth="1.6" />
          </pattern>
          <pattern id="ig-hatch-none" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="5" stroke={HATCH_NONE} strokeWidth="1.6" />
          </pattern>
          <pattern id="ig-ups" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
            <line x1="0" y1="0" x2="0" y2="5" stroke="var(--color-primary)" strokeWidth="1.4" />
          </pattern>
        </defs>
        {Array.from({ length: h1 - h0 + 1 }, (_, i) => h0 + i).map((h) => (
          <text key={h} x={L - 6} y={yH(h) + 3} textAnchor="end" fontSize={10} fill="var(--color-muted-foreground)">{hhmm(h * 60)}</text>
        ))}
        {cells.map((c) => {
          const x = xOf.get(c.day)!;
          return (
            <g key={`${c.day}:${c.h}`} onMouseEnter={() => setHover(c)} onClick={() => setPinned((p) => (p === c ? null : c))} className="cursor-pointer">
              <rect x={x + 1} y={yH(c.h) + 1} width={colW - 2} height={rowH - 2} rx={3} fill={fill(c)}
                stroke={hover === c ? 'var(--color-foreground)' : 'none'} strokeWidth={pinned === c ? 2 : 1} />
              {showLayout && c.lay && c.lay.bad >= BAD_SHARE && (
                <rect x={x + 1} y={yH(c.h) + 1} width={colW - 2} height={rowH - 2} rx={3} fill={c.q > 0 ? 'url(#ig-hatch-sold)' : 'url(#ig-hatch-none)'} pointerEvents="none" />
              )}
              {/* Апселл в целом: чем чаще в этот час что-то предлагали, тем гуще штриховка */}
              {over === 'upsell' && c.ups && c.ups.n > 0 && c.ups.any > 0 && (
                <rect x={x + 1} y={yH(c.h) + 1} width={colW - 2} height={rowH - 2} rx={3} fill="url(#ig-ups)"
                  fillOpacity={Math.min(1, 0.15 + c.ups.any / c.ups.n)} pointerEvents="none" />
              )}
              {/* «Предлагали эту позицию» — редкие события: точка с числом в углу ячейки */}
              {over === 'offer' && c.ups && c.ups.offer > 0 && (
                <g pointerEvents="none">
                  <circle cx={x + 7} cy={yH(c.h) + 7} r={5.5} fill="var(--color-primary)" stroke="var(--color-card)" strokeWidth={1.5} />
                  <text x={x + 7} y={yH(c.h) + 9.5} textAnchor="middle" fontSize={7} fill="var(--color-primary-foreground)">{c.ups.offer}</text>
                </g>
              )}
              {colW >= 22 && (mode === 'qty' ? c.q > 0 : c.top > 0 && Math.abs(c.q - c.top) >= 0.5) && (
                <text x={x + colW / 2} y={yH(c.h) + rowH / 2 + 3} textAnchor="middle" fontSize={9} fill="var(--color-foreground)" pointerEvents="none">
                  {mode === 'qty' ? (Number.isInteger(c.q) ? c.q : c.q.toFixed(1)) : fmtDiff(c.q - c.top)}
                </text>
              )}
            </g>
          );
        })}
        {det.gaps.map((g) => xOf.has(g.day) && (
          <rect key={`${g.day}:${g.from}`} x={xOf.get(g.day)! + 0.5} y={yM(g.from)} width={colW - 1} height={Math.max(3, yM(g.to) - yM(g.from))}
            rx={3} fill="none" stroke="rgb(220 38 38)" strokeWidth={2} strokeDasharray={g.sure === false ? '4 3' : undefined} pointerEvents="none" />
        ))}
        {(det.asks ?? []).map((a, i) => xOf.has(a.day) && (
          <circle key={`ask${i}`} cx={xOf.get(a.day)! + colW - 6} cy={yM(a.minute)} r={4} fill="rgb(234 88 12)" stroke="var(--color-card)" strokeWidth={1.5}
            className="cursor-pointer"
            onMouseEnter={() => setHover(cellAt(a.day, Math.floor(a.minute / 60)))}
            onClick={() => { const c = cellAt(a.day, Math.floor(a.minute / 60)); setPinned((p) => (p === c ? null : c)); }} />
        ))}
        {data.days.map((d) => {
          const s = dayShort(d.day), x = xOf.get(d.day)!;
          return (
            <g key={d.day}>
              <text x={x + colW / 2} y={T + H + 12} textAnchor="middle" fontSize={9} fill={s.we ? 'var(--color-foreground)' : 'var(--color-muted-foreground)'}>{s.n}</text>
              <text x={x + colW / 2} y={T + H + 23} textAnchor="middle" fontSize={8} fill="var(--color-muted-foreground)">{s.wd}</text>
            </g>
          );
        })}
      </svg>
      {hover && (
        <div className={cn('absolute right-2 top-2 rounded-lg border bg-card px-3 py-2 text-xs shadow-sm',
          hAsks.length ? 'w-[380px] max-w-[calc(100%-1rem)]' : 'max-w-[300px]',
          pinned ? 'z-10 ring-1 ring-primary/40' : 'pointer-events-none')}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 font-medium">{dayLabel(hover.day)}, {hhmm(hover.h * 60)}–{hhmm(hover.h * 60 + 60)}</div>
            {pinned ? (
              <button type="button" onClick={() => setPinned(null)} className="-mr-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Открепить">
                <X className="size-3.5" />
              </button>
            ) : <span className="text-[10px] text-muted-foreground">клик — закрепить</span>}
          </div>
          <div>{hover.q ? `${hover.q.toLocaleString('ru-RU')} шт` : 'не продавали'} · 5 лучших дней в этот час ≈ {hover.top.toFixed(1).replace('.', ',')} шт</div>

          {hover.lay && (
            <div className="text-muted-foreground">выкладка ≈ {hover.lay.pct} %{hover.lay.bad > 0 ? `, проблемная ${pct(hover.lay.bad)} времени` : ''}</div>
          )}
          {showLayout && det.rack != null && (
            <a href={layoutHref(pathname, sp, det.rack, hover.day, hover.h)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 font-medium text-primary underline-offset-2 hover:underline">
              Выкладка «{rackName}» {dayLabel(hover.day)}, {hhmm(hover.h * 60)}–{hhmm(hover.h * 60 + 60)} <ArrowUpRight className="size-3" />
            </a>
          )}
          {over && hover.ups && hover.ups.n > 0 && (
            <div className="text-muted-foreground">
              апселл: {hover.ups.any} из {hover.ups.n} разговоров ({pct(hover.ups.any / hover.ups.n)})
              {det.offer_word ? ` · предлагали «${det.offer_word}»: ${hover.ups.offer}` : ''}
            </div>
          )}
          {hGaps.map((g) => (
            <div key={g.from} className="text-red-600 dark:text-red-400">
              нет продаж {hhmm(g.from)}–{hhmm(g.to)}, обычно ≈ {g.expected_qty.toLocaleString('ru-RU')} шт · {rub(g.expected_rub)}
              {g.asked ? ` · гость спрашивал (${g.asked})` : g.sure === false ? ' · вероятно кончилось к вечеру' : ''}
            </div>
          ))}
          {hAsks.length > 0 && (
            <div className="mt-1.5 max-h-[340px] divide-y divide-border/60 overflow-auto border-t pt-1">
              {hAsks.map((a, i) => (
                <div key={i}>
                  <div className="pt-1 text-orange-700 dark:text-orange-300">
                    Гость спрашивал в {hhmm(a.minute)}: «{a.asked}» → {a.answer || 'нет'}
                  </div>
                  {a.clip ? <ClipExample clip={a.clip} onOpen={openConv} /> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Продажи еды по категориям по дням (#3210): «выросли хот-доги, но упали сэндвичи настолько же — плохо, а если
// в деньгах больше — уже лучше», поэтому штуки или рубли переключателем; выбранная позиция (или сумма выбранных) — пунктиром.
const CAT_COLORS = ['rgb(37 99 235)', 'rgb(234 88 12)', 'rgb(22 163 74)', 'rgb(147 51 234)', 'rgb(219 39 119)', 'rgb(13 148 136)'];
function CategorySalesChart({ data, item }: { data: ItemGapsDto; item: string | null }) {
  const [unit, setUnit] = React.useState<0 | 1>(0);   // 0 — шт, 1 — ₽
  const daily = (data.daily ?? []).filter((d) => d.checks > 0);
  const cats = [...new Set(daily.flatMap((d) => Object.keys(d.cats ?? {})))]
    .sort((a, b) => daily.reduce((s, d) => s + (d.cats?.[b]?.[1] ?? 0), 0) - daily.reduce((s, d) => s + (d.cats?.[a]?.[1] ?? 0), 0));
  if (daily.length < 2 || !cats.length) return null;
  const hasItem = !!item && daily.some((d) => d.item);
  // Столбцы стопкой (Алексей 29.10 #3233): высота = вся еда, куски = категории; цветов на всех не
  // хватает — хвост в серое «Другое», а не в новый цвет по кругу.
  const shown = cats.slice(0, cats.length > CAT_COLORS.length ? CAT_COLORS.length - 1 : CAT_COLORS.length);
  const rest = cats.slice(shown.length);
  const OTHER = 'Другое';
  const stack = rest.length ? [...shown, OTHER] : shown;
  const colorOf = (c: string) => (c === OTHER ? 'rgb(156 163 175)' : CAT_COLORS[shown.indexOf(c)]);
  const rows = daily.map((d) => {
    const r: Record<string, number | string> = { label: dayLabel(d.day) };
    for (const c of shown) r[c] = Math.round(d.cats?.[c]?.[unit] ?? 0);
    if (rest.length) r[OTHER] = Math.round(rest.reduce((s, c) => s + (d.cats?.[c]?.[unit] ?? 0), 0));
    r['Вся еда'] = Math.round(cats.reduce((s, c) => s + (d.cats?.[c]?.[unit] ?? 0), 0));
    if (hasItem) r[item!] = Math.round(d.item?.[unit] ?? 0);
    return r;
  });
  const fmt = (v: number) => (unit ? rub(v) : `${Math.round(v).toLocaleString('ru-RU')} шт`);
  return (
    <Card>
      <CardContent className="px-3 pb-2 pt-3">
        <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="text-sm font-medium">Продажи еды по дням</div>
          <div className="inline-flex overflow-hidden rounded-md border text-xs">
            {([[0, 'Штуки'], [1, 'Рубли']] as const).map(([v, label]) => (
              <button key={label} type="button" onClick={() => setUnit(v)}
                className={cn('px-2 py-1', unit === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>{label}</button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">Верхняя линия — вся еда за день, полосы — категории. Выросла одна категория, а другая упала столько же — гость просто перешёл; смотрите верхнюю линию в рублях.</div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="label" tick={TICK} interval="preserveStartEnd" />
            <YAxis tick={TICK} width={unit ? 56 : 36} tickFormatter={(v) => (unit ? `${Math.round(Number(v) / 1000)}к` : String(v))} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              cursor={{ fill: 'var(--color-muted)', opacity: 0.5 }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const r = payload[0].payload as Record<string, number>;
                return (
                  <div style={TOOLTIP_STYLE} className="px-2 py-1.5 text-xs">
                    <div className="mb-0.5 font-medium">{label} · вся еда {fmt(r['Вся еда'])}</div>
                    {[...stack].reverse().map((c) => (
                      <div key={c} className="flex items-center gap-1.5">
                        <span className="inline-block size-2 rounded-sm" style={{ background: colorOf(c) }} />
                        <span className="text-muted-foreground">{c}</span>
                        <span className="ml-auto pl-3 tabular-nums">{fmt(r[c] ?? 0)}</span>
                      </div>
                    ))}
                    {hasItem && <div className="mt-0.5 text-red-600">{item}: {fmt(r[item!] ?? 0)}</div>}
                  </div>
                );
              }}
            />
            {stack.map((c) => (
              <Area key={c} type="monotone" dataKey={c} stackId="food" stroke={colorOf(c)} strokeWidth={1.5}
                fill={colorOf(c)} fillOpacity={0.35} isAnimationActive={false} />
            ))}
            {hasItem && <Line type="monotone" dataKey={item!} stroke="rgb(220 38 38)" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
          {stack.map((c) => (
            <span key={c}><span className="mr-1 inline-block size-2 rounded-sm align-middle" style={{ background: colorOf(c) }} />{c}</span>
          ))}
          {hasItem && <span><span className="mr-1 inline-block h-0.5 w-3 bg-red-600 align-middle" />{item}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// Растёт ли доля чеков с едой и как с этим идёт апселл (Алексей 28.10 #3209): одна шкала — проценты.
function FoodUpsellChart({ data }: { data: ItemGapsDto }) {
  const rows = (data.daily ?? []).filter((d) => d.checks > 0).map((d) => ({
    day: d.day,
    label: dayLabel(d.day),
    food: Math.round((100 * d.food_checks) / d.checks),
    foodN: d.food_checks,
    checks: d.checks,
    upsell: d.upsell_share != null ? Math.round(d.upsell_share * 100) : null,
    conv: d.conversations ?? 0,
  }));
  if (rows.length < 2) return null;
  const hasUpsell = rows.some((r) => r.upsell != null);
  const first = rows.slice(0, Math.ceil(rows.length / 2)), last = rows.slice(Math.floor(rows.length / 2));
  const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / Math.max(1, xs.length);
  const dFood = avg(last.map((r) => r.food)) - avg(first.map((r) => r.food));
  return (
    <Card>
      <CardContent className="px-3 pb-2 pt-3">
        <div className="mb-1 flex flex-wrap items-baseline gap-x-3">
          <div className="text-sm font-medium">Чеки с едой{hasUpsell ? ' и апселл' : ''}</div>
          <div className="text-xs text-muted-foreground">
            доля чеков с едой {Math.abs(dFood) < 1 ? 'не меняется' : dFood > 0 ? `растёт: +${dFood.toFixed(1).replace('.', ',')} п.п.` : `падает: ${dFood.toFixed(1).replace('.', ',')} п.п.`} (вторая половина периода к первой)
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="label" tick={TICK} interval="preserveStartEnd" />
            <YAxis tick={TICK} unit=" %" domain={[0, 100]} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, k, p) => {
              const r = (p as { payload: (typeof rows)[number] }).payload;
              return k === 'food' ? [`${v} % (${r.foodN} из ${r.checks} чеков)`, 'чеки с едой'] : [`${v} % (${r.conv} разговоров)`, 'апселл'];
            }} />
            <Line type="monotone" dataKey="food" stroke="rgb(37 99 235)" strokeWidth={2} dot={{ r: 2 }} />
            {hasUpsell && <Line type="monotone" dataKey="upsell" stroke="rgb(234 88 12)" strokeWidth={2} dot={{ r: 2 }} connectNulls />}
          </LineChart>
        </ResponsiveContainer>
        <div className="flex gap-4 text-[11px] text-muted-foreground">
          <span><span className="mr-1 inline-block h-0.5 w-3 bg-blue-600 align-middle" />доля чеков с едой</span>
          {hasUpsell && <span><span className="mr-1 inline-block h-0.5 w-3 bg-orange-600 align-middle" />апселл: кассир что-то предложил (по звуку, где было что предлагать)</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// Только содержательное: часы с проблемной выкладкой и продажи в них против остальных. Коэффициент связи и
// «0 из 176 ч» не пишем (Алексей 29.10 #3250: «не надо это писать на сайте, дискредитируешь продукт»).
function LinkLine({ cells, rackName }: { cells: Cell[]; rackName: string }) {
  const k = layoutLink(cells);
  if (!k || k.nBad < 3 || k.badRatio == null || k.okRatio == null) return null;
  return (
    <div className="text-[11px] text-muted-foreground">
      «{rackName}»: проблемная выкладка {k.nBad} ч за период. В эти часы продаж {pct(k.badRatio)} от 5 лучших дней, в остальные {pct(k.okRatio)}.
    </div>
  );
}

export default function AnalyticsItemSalesTab({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const [sp, setSp] = useSearchParams();
  const { pathname } = useLocation();
  // Несколько позиций — повторный ?item= (Алексей 03.11): их продажи складываются; одна — как раньше
  const selItems = [...new Set(sp.getAll('item').filter(Boolean))];
  const item = selItems.length ? selItems.join(' + ') : null;
  const combo = selItems.length > 1;
  const [drinks, setDrinks] = React.useState(false);
  const [mode, setMode] = React.useState<'qty' | 'top5'>('qty');
  const [paint, setPaint] = React.useState<'loss' | 'last'>('loss');
  const rackParam = sp.get('rack');   // нет — стеллаж по категории позиции; 'none' — без выкладки
  const rackId = rackParam && rackParam !== 'none' ? Number(rackParam) : null;
  const overParam = sp.get('over');
  const over: Over = overParam === 'upsell' || overParam === 'offer' ? overParam : null;
  // Позиции или категории целиком (#3199): еда идёт по 1–3 шт в день, окно по одной позиции не доказать
  const by = sp.get('by') === 'category' ? 'category' : null;
  const list = useSWR(zoneId ? ['analytics:item-gaps', zoneId, from, to, by] : null,
    () => getItemGaps(zoneId, from, to, null, null, by).then((r) => r.data), { revalidateOnFocus: false });
  const det = useSWR(zoneId && item ? ['analytics:item-gaps', zoneId, from, to, item, rackId, by] : null,
    () => getItemGaps(zoneId, from, to, selItems, rackId, by).then((r) => r.data), { revalidateOnFocus: false, keepPreviousData: true });
  const setBy = (v: 'category' | null) => {
    const next = new URLSearchParams(sp);
    if (v) next.set('by', v); else next.delete('by');
    next.delete('item');
    next.delete('rack');
    setSp(next, { replace: true });
  };
  const setRack = (v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set('rack', v); else next.delete('rack');
    setSp(next, { replace: true });
  };
  const setOver = (v: string) => {
    const next = new URLSearchParams(sp);
    if (v === 'upsell' || v === 'offer') next.set('over', v); else next.delete('over');
    setSp(next, { replace: true });
  };
  const setItems = (names: string[], dropRack: boolean) => {
    const next = new URLSearchParams(sp);
    next.delete('item');
    for (const n of names) next.append('item', n);
    if (dropRack) next.delete('rack');
    setSp(next, { replace: true });
  };
  const select = (n: string) => setItems(n === item ? [] : [n], true);
  // стеллаж по умолчанию — первой позиции, поэтому при добавлении/удалении не сбрасываем
  const toggle = (n: string) => setItems(selItems.includes(n) ? selItems.filter((x) => x !== n) : [...selItems, n], false);
  if (list.isLoading && !list.data) return <div className="h-72 animate-pulse rounded-xl bg-muted" />;
  if (!list.data) {
    return <Card><CardContent className="p-0"><ServerStatusNotice variant="card" error={list.error} onRetry={() => list.mutate()} /></CardContent></Card>;
  }
  const data = list.data;
  if (!data.items.length) {
    return <Card><CardContent className="flex h-24 items-center justify-center text-sm text-muted-foreground">За период мало чеков, чтобы понять, когда позиции обычно берут</CardContent></Card>;
  }
  const items = data.items.filter((it) => drinks || !it.drink || selItems.includes(it.name));
  const total = items.reduce((s, it) => s + it.lost_rub, 0);
  const upper = items.reduce((s, it) => s + (it.upper_rub ?? it.lost_rub), 0);
  const netGap = items.reduce((s, it) => s + (it.network?.gap_rub ?? 0), 0);
  const hiddenDrinks = data.items.filter((it) => it.drink).length;
  const sel = combo ? undefined : data.items.find((it) => it.name === item);
  const comboRows = combo ? data.items.filter((it) => selItems.includes(it.name)) : [];
  const detail = det.data?.detail && det.data.detail.name === item ? det.data : null;
  const showLayout = rackParam !== 'none' && !!detail?.detail?.layout && detail.detail.rack != null;
  const rackName = detail?.detail?.racks.find((r) => r.id === detail.detail!.rack)?.name ?? '';
  return (
    <div className="space-y-4">
      {/* Переключатель на всю вкладку, не внутри карточки (Алексей 29.10 #3262: «я и не понял, что она была там»);
          в «Категориях» то же поле по часам строится по категории (#3255/#3263). */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Смотреть:</span>
        <div className="inline-flex overflow-hidden rounded-lg border text-sm">
          {([[null, 'Позиции'], ['category', 'Категории']] as const).map(([v, label]) => (
            <button key={label} type="button" onClick={() => setBy(v)}
              className={cn('px-3 py-1.5', by === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>{label}</button>
          ))}
        </div>
      </div>
      <Card>
        <CardContent className="space-y-2 px-4 py-3">
          {/* Три числа с подписями (#3259: «как это читать?») */}
          <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
            <div>
              <div className="text-xs text-muted-foreground">Недопродали за {dayLabel(data.date_from)} – {dayLabel(data.date_to)}</div>
              <div className="text-2xl font-bold tabular-nums">≈ {rub(total)}</div>
              <div className="text-[11px] text-muted-foreground">часы, когда позиции не было, а её обычно берут</div>
            </div>
            {upper > total + 1 && (
              <div>
                <div className="text-xs text-muted-foreground">С вечерним дефицитом</div>
                <div className="text-lg font-semibold tabular-nums">до {rub(upper)}</div>
                <div className="text-[11px] text-muted-foreground">если бы вечер шёл как в дни, когда позиция была до закрытия</div>
              </div>
            )}
            {netGap > 0 && (
              <div>
                <div className="text-xs text-muted-foreground">До середины ваших точек</div>
                <div className="text-lg font-semibold tabular-nums text-orange-600 dark:text-orange-400">ещё +{rub(netGap)}</div>
                <div className="text-[11px] text-muted-foreground">если бы на 100 чеков продавали как средняя ваша точка</div>
              </div>
            )}
          </div>
          <div className="text-[11px] leading-snug text-muted-foreground">
            Окно — подряд получасы без продаж, где по доле чеков с позицией в эти часы ждали хотя бы {data.min_expected.toLocaleString('ru-RU')} таких чеков.
            Часы, когда позицию обычно не берут или не выкладывают, не считаются. «До» — если бы вечер шёл как в дни, когда
            позиция была до закрытия (хронический вечерний дефицит; только для позиций от 5 шт в день).
            Позиция по 1–3 шт в день окна не даёт — смотрите «Категории». Каннибализацию сами не учитываем: часть спроса
            уходит на позиции в наличии — сложите заменители кнопкой «+» в строке таблицы (например, хот-дог + сэндвич-ролл)
            и смотрите их сумму.
          </div>
        </CardContent>
      </Card>

      <CategorySalesChart data={detail ?? data} item={by ? null : item} />
      <FoodUpsellChart data={data} />

      <Card>
        <CardContent className="px-3 pb-3 pt-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-md border text-xs">
                {([['loss', 'Недопродано'], ['last', 'Последняя продажа']] as const).map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setPaint(v)}
                    className={cn('px-2 py-1', paint === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>{label}</button>
                ))}
              </div>
              {/* Легенда вместо фразы (Алексей 29.10 #3265) */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {paint === 'loss' ? (
                  <span className="inline-flex items-center gap-1.5">
                    недопродано за день: мало
                    <span className="inline-flex overflow-hidden rounded-sm">
                      {[0.15, 0.35, 0.55, 0.75].map((a) => <span key={a} className="h-3 w-4" style={{ background: `rgb(239 68 68 / ${a})` }} />)}
                    </span>
                    много
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1"><span className="size-3 rounded-sm" style={{ background: 'rgb(239 68 68 / 0.6)' }} /> кончилась раньше обычного</span>
                    <span className="inline-flex items-center gap-1"><span className="size-3 rounded-sm" style={{ background: 'rgb(37 99 235 / 0.5)' }} /> продавали позже обычного</span>
                  </>
                )}
                <span className="inline-flex items-center gap-1 text-foreground/80"><MousePointerClick className="size-3.5" /> строка — разбор позиции по часам ниже</span>
              </div>
            </div>
            {hiddenDrinks > 0 && (
              // Подпись — что делает галка; почему скрыты — в подсказке (Алексей 30.10 #3424).
              <UiTooltip content={<span className="block max-w-72 text-xs leading-snug">Напитки готовят на месте, они не кончаются. Пропуски в их продажах — это тихие часы, а не нехватка, поэтому по умолчанию они скрыты.</span>}>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                  <input type="checkbox" checked={drinks} onChange={(e) => setDrinks(e.target.checked)} />
                  Показать напитки бара ({hiddenDrinks})
                </label>
              </UiTooltip>
            )}
          </div>
          <Heatmap data={data} items={items} selected={selItems} onSelect={select} onToggle={toggle} paint={paint} />
        </CardContent>
      </Card>

      {item && (
        <Card>
          <CardContent className="px-3 pb-3 pt-3">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
              {combo ? (
                <div className="flex flex-wrap items-center gap-1 text-sm font-medium">
                  {selItems.map((n, i) => (
                    <React.Fragment key={n}>
                      {i > 0 && <span className="text-muted-foreground">+</span>}
                      <span className="inline-flex items-center gap-0.5 rounded-md border bg-muted/40 py-0.5 pl-2 pr-0.5">
                        {n}
                        <button type="button" onClick={() => toggle(n)} aria-label={`Убрать «${n}»`}
                          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground">
                          <X className="size-3" />
                        </button>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              ) : <div className="text-sm font-medium">{item}</div>}
              {combo && (
                <div className="text-xs text-muted-foreground">
                  вместе {comboRows.reduce((a, it) => a + it.qty, 0).toLocaleString('ru-RU')} шт за{' '}
                  {data.days.filter((d) => comboRows.some((it) => (it.by_day[d.day]?.qty ?? 0) > 0)).length} дн.
                  {detail?.detail && detail.detail.gaps.length > 0 && <> · не брали ни одну, когда обычно берут, ≈ {rub(detail.detail.gaps.reduce((a, g) => a + g.expected_rub, 0))}</>}
                </div>
              )}
              {sel && (
                <div className="text-xs text-muted-foreground">
                  {sel.qty.toLocaleString('ru-RU')} шт за {sel.days_sold} дн. · ср. цена {rub(sel.avg_price)} · последняя обычно {hhmm(sel.last_median)} {fmtSwing(sel.last_swing)}
                  {sel.lost_rub > 0 && <> · недопродано ≈ {rub(sel.lost_rub)}</>}
                </div>
              )}
            </div>
            {detail?.detail && (
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <div className="inline-flex overflow-hidden rounded-md border">
                  {([['qty', 'Штуки'], ['top5', 'К 5 лучшим дням']] as const).map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setMode(v)}
                      className={cn('px-2 py-1', mode === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>{label}</button>
                  ))}
                </div>
{/* Стеллаж выбираем только для конкретной позиции; у категории он и так её (#3264) */}
                {!by && (
                <Select wrapperClassName="w-auto" className="h-8 bg-card pl-2 text-xs" value={showLayout ? String(detail.detail.rack) : 'none'}
                  onChange={(e) => setRack(e.target.value)}>
                  <option value="none">Выкладка: не показывать</option>
                  {detail.detail.racks.map((r) => (
                    <option key={r.id} value={r.id}>Выкладка: {r.name}{r.id === detail.detail!.default_rack ? ' (по категории)' : ''}</option>
                  ))}
                </Select>
                )}
                {detail.detail.upsell && (
                  <Select wrapperClassName="w-auto" className="h-8 bg-card pl-2 text-xs" value={over ?? ''} onChange={(e) => setOver(e.target.value)}>
                    <option value="">Речь: не накладывать</option>
                    <option value="upsell">Речь: апселл в целом</option>
                    {detail.detail.offer_word && <option value="offer">Речь: предлагали «{detail.detail.offer_word}»</option>}
                  </Select>
                )}
              </div>
            )}
            {detail ? <PeriodChart data={detail} mode={mode} showLayout={showLayout} over={detail.detail?.upsell ? over : null} rackName={rackName} /> : <div className="h-72 animate-pulse rounded-xl bg-muted" />}
            <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
              <div>
                Ячейка — продажи {combo ? 'выбранных позиций вместе' : 'позиции'} за час{mode === 'top5' ? ' и разница со средним 5 лучших дней в этот час; цвет — к тому, как обычно идёт этот час:' : ''}
                {mode === 'top5' && (
                  <>
                    {' '}<span className="inline-block size-3 rounded-sm align-[-2px]" style={{ background: top5Fill(0.3) }} />{' '}
                    <span className="inline-block size-3 rounded-sm align-[-2px]" style={{ background: top5Fill(0.7) }} /> ниже обычного для этого часа,{' '}
                    <span className="inline-block size-3 rounded-sm align-[-2px]" style={{ background: top5Fill(1) }} /> как обычно,{' '}
                    <span className="inline-block size-3 rounded-sm align-[-2px]" style={{ background: top5Fill(1.8) }} /> лучше обычного
                  </>
                )};
                красная рамка — окно, где позиции, похоже, не было (пунктир — вероятно кончилась к вечеру){showLayout ? '; штриховка — выкладка стеллажа проблемная больше половины часа:' : ''}
                {showLayout && (
                  <>
                    {' '}<HatchSwatch color={HATCH_SOLD} /> продажи были, <HatchSwatch color={HATCH_NONE} /> продаж не было
                    {detail?.detail && <HatchCounts cells={buildCells(detail, detail.detail)} />}
                  </>
                )}
                {over === 'upsell' ? '; синяя штриховка — апселл: чем гуще, тем чаще в этот час что-то предлагали' : ''}
                {over === 'offer' ? `; синяя точка — сколько раз предлагали «${detail?.detail?.offer_word ?? ''}»` : ''}.
              </div>
              {showLayout && detail?.detail && <LinkLine cells={buildCells(detail, detail.detail)} rackName={rackName} />}
              {detail?.detail?.rack && (
                <a href={layoutHref(pathname, sp, detail.detail.rack)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 font-medium text-primary underline-offset-2 hover:underline">
                  Выкладка «{rackName}» в статистике за этот период <ArrowUpRight className="size-3" />
                </a>
              )}
              {detail?.detail?.asks?.length
                ? <div><span className="inline-block size-2 rounded-full bg-orange-600 align-middle" /> — гость спрашивал позицию, а её не было (по звуку): {detail.detail.asks.length}.</div>
                : null}
            </div>
          </CardContent>
        </Card>
      )}

      {/* По голосу: явные недопродажи и допродажи (перенесено со «Разговоров», Алексей 03.11 #3567) */}
      <LostSalesCard zoneId={zoneId} from={from} to={to} onOpen={(id) => {
        const n = new URLSearchParams(sp);
        ['item', 'rack', 'over', 'by'].forEach((k) => n.delete(k));
        n.set('tab', 'conversations'); n.set('conv', String(id));
        window.open(`${window.location.pathname}?${n.toString()}`, '_blank', 'noopener');
      }} />
    </div>
  );
}
